import Promise from 'bluebird';
import loglevel from 'loglevel';
import { PRODUCT_API_CONFIGURATION } from 'constants';
import { queryString } from 'lib/format';
import toAbsoluteURL from 'lib/url-adapter/toAbsoluteURL';
import { newRelicEmitter, UNFULFILLED_PROMISE_EVENT} from 'lib/metrics/newRelicEventBus';
import request from './cache/requestCache';
import proxy from './requestProxy';
import fallback from './cache/fallbackCache';
import { Api, Device } from 'enums';
import appConfig from '../../../config';

const { Env: ApiEnv } = Api;

const log = loglevel.getLogger('PageAPI');

const pageApiConfig = appConfig.pageApi;

const DEFAULT_EXPIRATION = 60;
const DEFAULT_TIMEOUT = 10000;
const ONE_HOUR = 3600;

// we can set catalogPageTypes in app config if it is needed to be more manageable. 
const catalogPageTypes = new Set ([
    "competitionGroup",
    "compilation",
    "conference",
    "event",
    "film",
    "franchise",
    "institution",
    "league",
    "organizer",
    "other",
    "qualifier",
    "season",
    "series",
    "short",
    "sport",
    "tour",
    "tournament",
    "network"
]);

export const ERROR_CODES = {
    GENERIC_ERROR: 'GENERIC_ERROR',
    PAGE_NOT_CONFIGURED: 'PAGE_NOT_CONFIGURED',
    COUNTRY_NOT_SUPPORTED: 'COUNTRY_NOT_SUPPORTED',
    COUNTRY_AND_EDITION_CONFLICT: 'COUNTRY_AND_EDITION_CONFLICT'
};
// TODO: This could be something more elegant, like a bucket for the linear channels
export const ERROR_PAGE_RESPONSE = {
    page: {
        error: {
            errorCode: ERROR_CODES.GENERIC_ERROR,
            message: 'Page data not found'
        }
    }
};

const DEFAULT_CONFIGURATION = {
    paths: {
        base: '/api/product/{version}/watchespn/web',
        home: '/api/product/{version}/watchespn/web/home',
        event: '/api/product/{version}/watchespn/web/event',
        video: '/api/product/{version}/watchespn/web/video'
    }
};

export const config = { ...DEFAULT_CONFIGURATION, ...pageApiConfig };

export const getServiceUrl = (env = ApiEnv.PROD) => {
    return toAbsoluteURL(PRODUCT_API_CONFIGURATION[env].serviceUrl);
};

export const getPath = (path = 'base', version = 'v3') => {
    return `${config.paths[path].replace('{version}', version)}`;
};

export const getPageBaseUrl = (env = ApiEnv.PROD, version = 'v3') => {
    return `${getServiceUrl(env)}${getPath('base', version)}`;
};

export function getPageApiUrl(params) {
    const {name, qs, apiEnv, apiVersion, pathParamsArg} = params;
    /**
     * Jira ticket: WEWEB-2141
     * Please see comments in refreshBuckets where pageName has been overridden to be correct page name
     * refreshBuckets is called when user is logged in which is triggered down getPage being called.
     * When user logged in to collection page, pageApi url returns ".../collection/5060?..."
     * and causes 404 from the api. We need to update name from 'collection' to 'bucket' and append 
     * bucketId in the url
     */
    if (name === 'bucket' && pathParamsArg) {
        return `${getPageBaseUrl(apiEnv, apiVersion)}/${name}${qs}&bucketId=${pathParamsArg}`;
    } 

    let type = name;
    if (catalogPageTypes.has(name)) {
        type = 'catalog'
    }
    const pathParams = pathParamsArg ? `/${pathParamsArg}` : '';
    return`${getPageBaseUrl(apiEnv, apiVersion)}/${type}${pathParams}${qs}`;
}

export function getPage({
    apiEnv,
    apiVersion,
    authStates,
    authNetworks,
    entitlements,
    favoriteTeam,
    favoriteSport,
    countryCode,
    deviceType = Device.DESKTOP,
    zipcode,
    pathParams: pathParamsArg,
    lang,
    tz,
    name,
    packageId,
    tier,
    edition, // Optional query param, will override app edition
    sessionToken,
    features
}) {
    const isShortStopLive = (name === 'shortstoplive');
    const ssBust = isShortStopLive ? (Math.floor(Math.random() * 1000)) : null;
    const qs = queryString({
        authStates,
        authNetworks,
        entitlements,
        favoriteTeam,
        favoriteSport,
        lang,
        tz,
        deviceType,
        ssBust,
        countryCode,
        zipcode,
        packageId,
        tier,
        edition,
        features
    });

    const url = getPageApiUrl({name, qs, apiEnv, apiVersion, pathParamsArg});

    log.debug(`getPage: ${url}`);

    return request.get(url)
        .set('Accept-Language', `${lang}-${countryCode}`)
        .set('dss-session-token', sessionToken)
        .expiration(isShortStopLive ? 0 : DEFAULT_EXPIRATION)
        .use(proxy)
        .timeout(DEFAULT_TIMEOUT)
        .then(({ body }) => {
            fallback.set(url, body);
            return body;
        })
        .catch((err) => {
            log.error(`getPage: ${url}`, err.status);
            newRelicEmitter.emit(UNFULFILLED_PROMISE_EVENT, err);
            if (err.status >= 400 && err.status < 500) {
                return (err.body && err.body.page && err.body.page.error) ? err.body : ERROR_PAGE_RESPONSE;
            }
            return fallback.get(url).then(response => response || undefined);
        });
}

export function getEvent({
    apiEnv,
    apiVersion,
    countryCode,
    authStates,
    authNetworks,
    entitlements,
    favoriteTeam,
    favoriteSport,
    deviceType = Device.DESKTOP,
    zipcode,
    lang,
    tz,
    packageId,
    tier,
    query = {},
    features
}) {
    // essentially our schema for event page requests
    const { airingId, eventId, id, network, sourceLang, status } = query;
    const eventQuery = { airingId, eventId, id, network, sourceLang, status };

    const qs = queryString({
        ...eventQuery,
        authStates,
        authNetworks,
        entitlements,
        favoriteTeam,
        favoriteSport,
        lang,
        tz,
        deviceType,
        countryCode,
        zipcode,
        packageId,
        tier,
        features
    });
    const url = `${getServiceUrl(apiEnv)}${getPath('event', apiVersion)}${qs}`;

    if (!Object.keys(query || {}).length) {
        return Promise.reject(new Error('No query params to get an event.'));
    }

    log.debug(`getEvent: ${url}`);

    return request.get(url)
        .set('Accept-Language', `${lang}-${countryCode}`)
        .use(proxy)
        .timeout(DEFAULT_TIMEOUT)
        .then(({ body }) => {
            fallback.set(url, body);
            return body;
        })
        .catch((err) => {
            log.error(`getEvent: ${url}`, err.originalError);
            newRelicEmitter.emit(UNFULFILLED_PROMISE_EVENT, err);
            return fallback.get(url).then(response => response || undefined);
        });
}

// video endpoint is slightly different and uses a path param
export function getVideo({
    apiEnv,
    apiVersion,
    countryCode,
    deviceType = Device.DESKTOP,
    authStates,
    authNetworks,
    entitlements,
    favoriteTeam,
    favoriteSport,
    lang,
    sourceLang,
    tz,
    packageId,
    tier,
    pathParams: pathParamsArg,
    query = {},
    features
}) {
    const qs = queryString({
        ...query,
        authStates,
        authNetworks,
        entitlements,
        favoriteTeam,
        favoriteSport,
        lang,
        sourceLang,
        tz,
        deviceType,
        countryCode,
        packageId,
        tier,
        features
    });
    const pathParams = pathParamsArg ? `/${pathParamsArg}` : '';
    const url = `${getServiceUrl(apiEnv)}${getPath('video', apiVersion)}${pathParams}${qs}`;

    if (!pathParamsArg) {
        return Promise.reject(new Error('No params to get a video.'));
    }

    log.debug(`getVideo: ${url}`);

    return request.get(url)
        .set('Accept-Language', `${lang}-${countryCode}`)
        .use(proxy)
        .timeout(DEFAULT_TIMEOUT)
        .then(({ body }) => {
            fallback.set(url, body);
            return body;
        })
        .catch((err) => {
            log.error(`getVideo: ${url}`, err);
            newRelicEmitter.emit(UNFULFILLED_PROMISE_EVENT, err);
            return fallback.get(url).then(response => response || undefined);
        });
}

export function getBucketPage({
    apiEnv,
    apiVersion,
    authStates,
    authNetworks,
    entitlements,
    favoriteTeam,
    favoriteSport,
    bucketId,
    countryCode,
    deviceType = Device.DESKTOP,
    zipcode,
    groupId,
    ignores,
    lang,
    tz,
    packageId,
    tier,
    sessionToken,
    features
}) {
    const qs = queryString({
        authStates,
        authNetworks,
        entitlements,
        favoriteTeam,
        favoriteSport,
        bucketId,
        countryCode,
        deviceType,
        zipcode,
        groupId,
        ignores,
        lang,
        tz,
        packageId,
        tier,
        features
    });

    const url = `${getPageBaseUrl(apiEnv, apiVersion)}/bucket${qs}`;

    log.debug(`getBucketPage: ${url}`);

    return request.get(url)
        .set('Accept-Language', `${lang}-${countryCode}`)
        .set('dss-session-token', sessionToken)
        .use(proxy)
        .timeout(DEFAULT_TIMEOUT)
        .then(({ body }) => {
            fallback.set(url, body);
            return body;
        })
        .catch((err) => {
            log.error(`getBucketPage: ${url}`, err.message);
            newRelicEmitter.emit(UNFULFILLED_PROMISE_EVENT, err);
            return fallback.get(url).then(response => response || undefined);
        });
}

// #TODO: check the format of the url param and reject if invalid
export function getPageByFullUrl({
    countryCode,
    deviceType = Device.DESKTOP,
    url: urlArg,
    sessionToken,
    features
}) {
    const qs = queryString({ deviceType, countryCode, features });
    const url = `${urlArg}${qs}`;

    log.debug(`getUrl: ${url}`);

    if (url) {
        return request.get(url)
            .use(proxy)
            .set('dss-session-token', sessionToken)
            .timeout(DEFAULT_TIMEOUT)
            .then(({ body }) => {
                fallback.set(url, body);
                return body;
            })
            .catch(err => {
                log.error(`getUrl: ${url}`, err.status);
                newRelicEmitter.emit(UNFULFILLED_PROMISE_EVENT, err);
                if (err.status === 404) {
                    return ERROR_PAGE_RESPONSE;
                }
                return fallback.get(url).then(response => response || undefined);
            });
    }
    return Promise.reject(new Error('No url supplied'));
}

export function getPlaybackMeta(urlArg) {
    let url = urlArg;
    // Replacing the playback url with https due to server side making request with http and dehydrated to the client side.
    if (process.browser && url.indexOf('http://') === 0) {
        url = url.replace('http://', 'https://');
    }

    log.debug(`getPlaybackMeta: ${url}`);

    return request.get(`${url}${url.indexOf('?') === -1 ? '?' : '&'}rand=${Date.now()}`)
        .expiration(0)
        .use(proxy)
        .timeout(DEFAULT_TIMEOUT)
        .then(({ body }) => {
            fallback.set(url, body);
            return body;
        })
        .catch((err) => {
            log.error(`getPlaybackMeta: ${url}`, err.originalError);
            newRelicEmitter.emit(UNFULFILLED_PROMISE_EVENT, err);
            return fallback.get(url).then(response => response || undefined);
        });
}

export function getCategories(apiEnv = ApiEnv.PROD, { countryCode = 'US', lang = 'en'}) {
    const qs = queryString({ countryCode, lang });
    const url = `${getServiceUrl(apiEnv)}/api/product/v3/watchespn/web/collection/alias/schedule_categories${qs}`;
    return request(url)
        .use(proxy)
        .then((response) => {
            const categories = response.body.nodes || [];
            fallback.set(url, categories);
            return categories;
        })
        .catch((err) => {
            log.error('getCategories:', err.originalError);
            return fallback.get(url);
        });
}

export function getNetworks(apiEnv = ApiEnv.PROD, { countryCode = 'US', lang = 'en'}) {
  const qs = queryString({ countryCode, lang });
  const url = `${getServiceUrl(apiEnv)}/api/product/v3/watchespn/web/collection/alias/schedule_networks${qs}`;
  return request(url)
      .use(proxy)
      .then((response) => {
          const networks = response.body.nodes || [];
          fallback.set(url, networks);
          return networks;
      })
      .catch((err) => {
          log.error('getNetworks:', err.originalError);
          return fallback.get(url);
      });
}

export function getPickerStreams(url) {
    if (url && typeof url === 'string') {
        if (window.location.protocol === 'https:') {
            url = url.replace('http://', 'https://');
        }
        return request.get(url)
        .use(proxy)
        .timeout(DEFAULT_TIMEOUT)
        .then(({ body }) => {
            fallback.set(url, body);
            return body;
        })
        .catch((err) => {
            log.error(`getPicker: ${url}`, err.originalError);
            newRelicEmitter.emit(UNFULFILLED_PROMISE_EVENT, err);
            return fallback.get(url).then(response => response || undefined);
        });
    }
}




// WEBPACK FOOTER //
// ./app/lib/api/PageAPI.js